/**
 * Generic feature zone shape — covers cake table, gift table, dessert
 * station, donut wall, photo booth, DJ booth, and cocktail table. Most
 * share the same "labeled rectangle with an icon glyph" visual treatment,
 * varying only in color accent and icon — except cocktail tables and the
 * cake table, which render as a circle instead: a round high-top cocktail
 * table and a round cake table are how these actually look in real life,
 * and a square render was a real, reported mismatch with what
 * coordinators expect to see on the floor plan.
 */

import { Group, Rect, Circle, Text } from 'react-konva';
import { useState } from 'react';
import type Konva from 'konva';
import type { LayoutObject, ObjectCatalogEntry, ObjectType } from '../../../domain/types';
import { FEET_TO_PX } from '../canvasConstants';

const ICONS: Record<string, string> = {
  cake_table: '\u{1F382}',
  gift_table: '\u{1F381}',
  dessert_station: '\u{1F370}',
  donut_wall: '\u{1F369}',
  photo_booth: '\u{1F4F8}',
  dj_booth: '\u{1F3A7}',
  cocktail_table: '\u{1F377}',
};

const ACCENTS: Partial<Record<ObjectType, string>> = {
  cake_table: '#D9B8AE',
  gift_table: '#8FA38B',
  dessert_station: '#D9B8AE',
  donut_wall: '#D9B8AE',
  photo_booth: '#8FA38B',
  dj_booth: '#6E8569',
  cocktail_table: '#E6CC97',
};

/** Object types that render as a round shape instead of the default
 * rounded rectangle. Diameter is the smaller of width/height, so even if
 * a catalog entry's dimensions aren't perfectly square, this degrades to
 * a sensible circle rather than stretching into an ellipse. */
const ROUND_TYPES = new Set<ObjectType>(['cocktail_table', 'cake_table']);

interface FeatureZoneProps {
  object: LayoutObject;
  catalogEntry: ObjectCatalogEntry;
  isSelected: boolean;
  onSelect: (e?: Konva.KonvaEventObject<Event>) => void;
  onDragEnd: (x: number, y: number) => void;
  /** See ObjectRenderer.tsx and RoundTable.tsx for why this exists —
   * counter-rotates the icon and label only, against the room's view
   * orientation specifically. */
  roomRotationDegrees: number;
}

export function FeatureZone({ object, catalogEntry, isSelected, onSelect, onDragEnd, roomRotationDegrees }: FeatureZoneProps) {
  const widthPx = catalogEntry.defaultWidthFt * FEET_TO_PX;
  const heightPx = catalogEntry.defaultHeightFt * FEET_TO_PX;
  const accent = ACCENTS[object.type] ?? '#8FA38B';
  const icon = ICONS[object.type] ?? '';
  const isRound = ROUND_TYPES.has(object.type);
  const radiusPx = Math.min(widthPx, heightPx) / 2;
  // See RoundTable.tsx for why shadow rendering is suppressed during drag.
  const [isDragging, setIsDragging] = useState(false);

  function handleDragStart(e: Konva.KonvaEventObject<DragEvent>) {
    setIsDragging(true);
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'grabbing';
  }

  function handleDragEnd(e: Konva.KonvaEventObject<DragEvent>) {
    setIsDragging(false);
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'grab';
    const node = e.target;
    onDragEnd(node.x() / FEET_TO_PX, node.y() / FEET_TO_PX);
  }

  return (
    <Group
      id={object.id}
      x={object.x * FEET_TO_PX}
      y={object.y * FEET_TO_PX}
      rotation={object.rotation}
      draggable={!object.locked}
      onClick={onSelect}
      onTap={onSelect}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      opacity={object.locked ? 0.85 : 1}
      onMouseEnter={(e) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = object.locked ? 'default' : 'grab';
      }}
      onMouseLeave={(e) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = 'default';
      }}
    >
      {isRound ? (
        <Circle
          radius={radiusPx}
          fill="#FFFEFC"
          stroke={isSelected ? '#C9A35C' : accent}
          strokeWidth={isSelected ? 3 : 1.5}
          shadowColor="#2A2522"
          shadowOpacity={isDragging ? 0 : 0.1}
          shadowBlur={isDragging ? 0 : 4}
          shadowOffsetY={isDragging ? 0 : 1}
        />
      ) : (
        <Rect
          x={-widthPx / 2}
          y={-heightPx / 2}
          width={widthPx}
          height={heightPx}
          fill="#FFFEFC"
          stroke={isSelected ? '#C9A35C' : accent}
          strokeWidth={isSelected ? 3 : 1.5}
          cornerRadius={4}
          dash={object.type === 'photo_booth' ? [6, 4] : undefined}
          shadowColor="#2A2522"
          shadowOpacity={isDragging ? 0 : 0.1}
          shadowBlur={isDragging ? 0 : 4}
          shadowOffsetY={isDragging ? 0 : 1}
        />
      )}

      <Text
        text={icon}
        fontSize={Math.min(widthPx, heightPx) * 0.4}
        width={widthPx}
        height={heightPx * 0.65}
        offsetX={widthPx / 2}
        offsetY={heightPx / 2}
        rotation={-roomRotationDegrees}
        align="center"
        verticalAlign="middle"
        listening={false}
      />

      <Text
        text={object.label}
        fontSize={10}
        fontFamily="Inter, sans-serif"
        fill="#6B6258"
        width={widthPx}
        height={14}
        y={heightPx / 2 - 7}
        offsetX={widthPx / 2}
        offsetY={7}
        rotation={-roomRotationDegrees}
        align="center"
        verticalAlign="middle"
        listening={false}
      />

      {object.locked && (
        <Text
          text={'\u{1F512}'}
          fontSize={13}
          x={0}
          y={-(heightPx / 2 + 14)}
          offsetX={6.5}
          offsetY={6.5}
          listening={false}
        />
      )}
    </Group>
  );
}
